var _ = require("lodash");
var { StringId, ObjectId, FunctionId } = require("../../../typing/typing");

/***Thismodulecollectsallthefunctiontodeclareanduse*{@linkmodule:Lambda/Constructors~ExternalExternal}lambdaterms
 *   with complex structure.
 *
 * An external object may contain lambda terms which should be processed
 *   by the Lambda system (when reducing, exporting etc)
 *   with respect of External object's semantic.
 *
 * There are basic problems:
 * 1. beta reduction is about replacing a variable (formal argument)
 *   with a term (actual argument). Therefore, there must be a way
 *   to replace a variable in External object with a term
 *   if the variable is statically bound.
 * 2. reduction is usually closed under term structure.
 *   It means that a reduction must be able to pass through a structure of
 *   an External object and reduce its subterms if the object's semantic allows.
 * 3. If beta reduction is implemented with explicit replacement,
 *   actual argument's free variables must be renamed to avoid name conflicts 
 *   with abstractors in which scope they are inserted.
 *   Therefore, an External object must provide a set of its free variables.
 * 4. If JS-Typing contract system is used,
 *   a type of each object should be defined.
 *
 * #1, #2 and partially #3 are solved if each External object provides `map`
 *   function that applies arbitrary function on each subterm and returns
 *   another external object based on initial one
 *   with subterms replaced with function's result.
 * #3 also suppose each External object to provide an array of free variables.
 *
 * It is natural to assume that all the External objects can be categorized
 *   into finite number of groups, each group has its own implementation
 *   of mentioned methods.
 * Each group is identified by a string called `objType`
 *   (external object type) which constitutes together with an object
 *   an External lambda term.
 * If only an object were used as an External lambda term,
 *   one would face a problem of determining which functions should be used
 *   to map a substitution, remaning etc.
 *
 * @module Lambda/Externals
 */

var Types = require("../Types");
var isLambdaTerm = require("./Predicates").isLambdaTerm;
var isLambdaObject = require("./Predicates").isLambdaObject;

/**
 * `ExternalObjectInterpretation` is an associative array of `objType`
 *   whose values are objects with the following fields:
 * - `map(obj, F)` is a functor map on nested lambda terms.
 *   + `obj` is an object on which the function is mapped
 *   + `F` is a function of lambda term
 *   + it returns an array of `F(X)` for each lambda term `X` in the object
 * - `traverse` is a natural transformation from this functor to a list,
 * it traverses an object collecting properties of nested lambda terms.
 *   + the only argument refers to an object
 *   + it returns an array of lambda terms
 * - `id` is an identification function, see JS typing section.
 * - `importFromJSON` is a function that converts an object to JSON.
 * - `exportToJSON` is a function that parses an object from JSON format.
 *
 * Some of these fields may be undefined.
 * - If `map` is undefined, a constant `this` is used instead.
 *   The associated functor is thus a constant functor
 *   that maps each morphism to an identity.
 * - If `traverse` is undefined, a tricky fake is used instead:
 *   + First, a local variable `arr = []` is initialized.
 *   + Second, a function of lambda subterm `t` is mapped by `map` function.
 *   + This function pushes each subterm `t` to `arr`.
 *   + Finally, when `map` finishes, `arr` is returned.
 * - If `id` is undefined, an identity function is used instead.
 *   That means an external object of given `objType` never fails type checking.
 * - `importFromJSON` and `exportToJSON` treat by default an object
 *   as a container of lambda terms.
 *
 * @summary an object containing an information about types of external terms.
 */
var ExternalObjectInterpretation = {};

ExternalObjectInterpretation.String = {};
ExternalObjectInterpretation.Number = {};
ExternalObjectInterpretation.Function = {};
ExternalObjectInterpretation.Object = {};

/**
 * External map function getter.
 * @param {string} objType
 * @return map function if defined, constant function otherwise
 */
function getExternalMap(objType) {
  StringId(objType);
  var i = ExternalObjectInterpretation[objType];
  if(!i) return defaultExternalMap;
  ObjectId(i);
  return i.map || defaultExternalMap;
}

function defaultExternalMap(obj, func) {
  return obj;
}

/**
 * External traverse function getter.
 * @param {string} objType
 * @return traverse function if defined,
 *   subterm collection based on map otherwise
 */
function getExternalTraverse(objType) {
  StringId(objType);
  var i = ExternalObjectInterpretation[objType];
  if(!i) return defaultExternalTraverse;
  if(i.traverse) return i.traverse;
  if(!i.map) return defaultExternalTraverse;
  return function defaultExternalTraverseBasedOnMap(obj) {
    var arr = [];
    i.map(obj, function(t) { arr.push(t); return t; });
    return arr;
  };
}

function defaultExternalTraverse() {
  return [];
}

/**
 * External identification getter.
 * @param {string} objType
 * @return identification if defined, identity otherwise
 */
function getExternalId(objType) {
  var i = ExternalStandardImplementation[StringId(objType)];
  if(i && i.id) return i.id;
  return _.identity;
}

/**
 * @callback ImportFunction
 * @param {JSON} obj - JSON representation
 * @param {Object} [env={}] - an environment
 * @param env.x - an object the variable `x` will be replaced to (for every `x`)
 * @param {ImportFunction} [transformer=id] - a function to import subterms
 * @return parsed object
 */

/**
 * External import function getter.
 * @param {string} objType
 * @return {module:Lambda/Externals~ImportFunction}
 */
function getExternalImport(objType) {
  StringId(objType);
  var i = ExternalObjectInterpretation[objType];
  if(!i) return defaultExternalImport;
  if(i.importFromJSON) return i.importFromJSON;
  if(!i.map) return defaultExternalImport;
  return function gefaultExternalImportBasedOnMap(obj, env, traverse) {
    return i.map(obj, function(term) { return traverse(term, env); });
  };
}

function defaultExternalImport(obj) { return obj; }

/**
 * @callback ExportFunction
 * @param {*} obj - an object
 * @param {string[]} [prefered=[]] - an array of prefered names of variables
 * @param {string[]} [forbidden=[]] - an array of variable names that will
 *   never be used
 * @param {ExportFunction} [transformer=id] - a function to export subterms
 * @return JSON
 */

/**
 * External export function getter.
 * @param {string} objType
 * @return {module:Lambda/Externals~ExportFunction}
 */
function getExternalExport(objType) {
  StringId(objType);
  var i = ExternalObjectInterpretation[objType];
  if(!i) return defaultExternalExport;
  if(i.exportToJSON) return i.exportToJSON;
  if(!i.map) return defaultExternalExport;
  return function gefaultExternalExportBasedOnMap(obj, p, f, e) {
    return i.map(obj, function(term) { return e(term, p, f, e); });
  };
}

function defaultExternalExport(obj) { return obj; }


/**
 * A collection of function that are useful
 *   to implement External object interpretation.
 * @module Lambda/Externals/ExternalStandardImplementation
 */
var ExternalStandardImplementation = {
  
  /**
   * apply a function `f` to each fields that is a lambda object.
   * @param {Object} obj - an object
   * @param {Function} f - a function of lambda object
   * @return an object with the same set of keys
   * @memberof module:Lambda/Externals/ExternalStandardImplementation
   */
  mapOnFields: function(obj, f) {
      console.log(isLambdaObject);
      return _.mapValues(obj, function(v) {
        return isLambdaObject(v) ? f(v) : v;
      });
  },
  
  /**
   * apply a function `f` to each fields that is a lambda term.
   * @param {Object} obj - an object
   * @param {Function} f - a function of lambda term
   * @return an object with the same set of keys
   * @memberof module:Lambda/Externals/ExternalStandardImplementation
   */
  mapOnTermField: function(obj, f) {
      var isLambdaJSON = require("./Predicates").isLambdaJSON;
      ObjectId(obj);
      FunctionId(f);
      return _.mapValues(obj, function(v) {
        return isLambdaTerm(v) ? f(v) : (isLambdaJSON(v) ? f(v) : v);
      });
  }
  
};


/**
 * Restores an (string) objType of External object that has given type.
 * @param type - a type
 * @return objType (string)
 */
function getExternalObjType(type) {
  if(type instanceof Types.BasicType) return type.name;
  if(_.isString(type)) return type;
  if(type instanceof Types.FunctionType) return "Function";
  if(type instanceof Types.ListType) return "List";
  console.error(type);
  console.error("Cannot restore objType by given term type. Using Object");
  return "Object";
}

module.exports = {
  ExternalObjectInterpretation: ExternalObjectInterpretation,
  getExternalMap: getExternalMap,
  getExternalTraverse: getExternalTraverse,
  getExternalId: getExternalId,
  getExternalImport: getExternalImport,
  getExternalExport: getExternalExport,
  getExternalObjType: getExternalObjType,
  ExternalStandardImplementation: ExternalStandardImplementation
};
